import React, { useEffect, useMemo } from 'react';
import { Container } from 'reactstrap';
import { Helmet } from 'react-helmet';
import { observer } from 'mobx-react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
    Block,
    FlexDiv,
    Heading2,
    Heading3,
    palette,
    SpaceBetweenDiv,
    StatusBadge,
    TextDefault,
    TextTiny,
    ThemedButton,
} from '@awarego-ui/components';
import HeaderSection from '../../components/header/HeaderSection';
import SubHeaderText from '../../components/header/SubHeaderText';
import SubHeaderComponentsList from '../../components/header/SubHeaderComponentsList';
import { useStores } from '../../hooks/use-stores';
import AwareExposeSubNav from './AwareExposeSubNav';

const MetricsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 16px;
    @media (max-width: 900px) {
        grid-template-columns: 1fr 1fr;
    }
`;

const MetricCard = styled.div`
    background: ${palette.graphite.white};
    border: 1px solid ${palette.graphite.silver};
    border-radius: 8px;
    padding: 14px 16px;
`;

const MetricLabel = styled.div`
    font-size: 11px;
    color: ${palette.graphite.charcoal};
    margin-bottom: 4px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
`;

const MetricValue = styled.div`
    font-size: 24px;
    font-weight: 700;
    color: ${({ $color }) => $color || palette.graphite.onyx};
    line-height: 1;
`;

const MetricSub = styled.div`
    font-size: 11px;
    color: ${palette.graphite.charcoal};
    margin-top: 4px;
`;

const SplitRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    margin-bottom: 14px;
    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

const DistBar = styled.div`
    display: flex;
    height: 28px;
    border-radius: 6px;
    overflow: hidden;
    margin: 12px 0 8px;
    gap: 2px;
    background: ${palette.graphite.silver};
`;

const DistSegment = styled.div`
    height: 100%;
    width: ${({ $w }) => `${$w}%`};
    background: ${({ $color }) => $color};
    transition: width 0.4s ease;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th {
        text-align: left;
        font-size: 10px;
        font-weight: 600;
        color: ${palette.graphite.charcoal};
        text-transform: uppercase;
        letter-spacing: 0.05em;
        padding: 8px 12px;
        border-bottom: 1px solid ${palette.graphite.silver};
    }
    td {
        padding: 10px 12px;
        border-bottom: 1px solid ${palette.graphite.cloud};
        color: ${palette.graphite.heavyMetal};
        vertical-align: middle;
    }
    tr:hover td {
        background: ${palette.graphite.cloud};
    }
`;

function scoreColor(score) {
    if (score == null) return palette.graphite.charcoal;
    if (score >= 70) return palette.vibrant.red;
    if (score >= 50) return palette.vibrant.orange;
    return palette.evergreen.aspargus;
}

function levelBadge(level) {
    if (level === 'Critical' || level === 'High')
        return (
            <StatusBadge text={level || 'High'} color={palette.vibrant.red} />
        );
    if (level === 'Medium')
        return (
            <StatusBadge text="Medium" color={palette.vibrant.orange} />
        );
    if (level === 'Low')
        return (
            <StatusBadge text="Low" color={palette.evergreen.aspargus} />
        );
    return <StatusBadge text="—" color={palette.graphite.charcoal} />;
}

function statusBadge(status) {
    if (status === 'active')
        return (
            <StatusBadge
                text="In progress"
                color={palette.vibrant.orange}
            />
        );
    if (status === 'completed')
        return (
            <StatusBadge
                text="Complete"
                color={palette.evergreen.aspargus}
            />
        );
    if (status === 'cancelled')
        return (
            <StatusBadge
                text="Cancelled"
                color={palette.graphite.charcoal}
            />
        );
    return (
        <StatusBadge text="Draft" color={palette.graphite.charcoal} />
    );
}

function AwareExposeDashboard() {
    const { store, awareExposeStore } = useStores();
    const navigate = useNavigate();
    const { currentCompany } = store;
    const { dashboard, loadingDashboard } = awareExposeStore;

    useEffect(() => {
        if (currentCompany && currentCompany.company_id) {
            awareExposeStore.loadDashboard(currentCompany.company_id);
        }
    }, [currentCompany && currentCompany.company_id]);

    const totals = (dashboard && dashboard.totals) || {};
    const distribution = (dashboard && dashboard.distribution) || {};
    const recent = (dashboard && dashboard.recent_campaigns) || [];
    const topHigh = (dashboard && dashboard.top_high_risk) || [];

    const distTotal = useMemo(() => {
        return ['Critical', 'High', 'Medium', 'Low'].reduce(
            (acc, k) => acc + (distribution[k] || 0),
            0
        );
    }, [distribution]);

    const distSeg = (key) => {
        if (!distTotal) return 0;
        return ((distribution[key] || 0) / distTotal) * 100;
    };

    return (
        <Container className="manager-content">
            <Helmet>
                <title>AwareExpose</title>
            </Helmet>
            <HeaderSection
                heading={'AwareExpose'}
                subHeadings={
                    <SubHeaderComponentsList>
                        <SubHeaderText
                            text={`Personal exposure risk programme. Run campaigns, see how visible your employees are to attackers, and track your organisation's overall exposure score over time.`}
                        />
                    </SubHeaderComponentsList>
                }
                buttons={
                    <ThemedButton
                        text="+ New campaign"
                        mode={'primary'}
                        onClick={() =>
                            navigate('/aware-expose/campaigns/new')
                        }
                        $noWrap
                    />
                }
            />

            <AwareExposeSubNav active="" />

            <MetricsGrid>
                <MetricCard>
                    <MetricLabel>Employees assessed</MetricLabel>
                    <MetricValue>
                        {Number(totals.assessed_count || 0)}
                    </MetricValue>
                    <MetricSub>
                        of {Number(totals.audience_total || 0)} invited
                    </MetricSub>
                </MetricCard>
                <MetricCard>
                    <MetricLabel>Avg exposure score</MetricLabel>
                    <MetricValue
                        $color={scoreColor(totals.avg_score)}
                    >
                        {totals.avg_score == null
                            ? '—'
                            : Number(totals.avg_score)}
                    </MetricValue>
                    <MetricSub>Across completed runs</MetricSub>
                </MetricCard>
                <MetricCard>
                    <MetricLabel>High risk employees</MetricLabel>
                    <MetricValue $color={palette.vibrant.red}>
                        {Number(totals.high_risk_count || 0)}
                    </MetricValue>
                    <MetricSub>Critical + High</MetricSub>
                </MetricCard>
                <MetricCard>
                    <MetricLabel>Campaigns</MetricLabel>
                    <MetricValue>{recent.length || 0}</MetricValue>
                    <MetricSub>Recent activity</MetricSub>
                </MetricCard>
            </MetricsGrid>

            <SplitRow>
                <Block padding="20" column>
                    <Heading3>Risk distribution</Heading3>
                    <TextTiny lighter>
                        {distTotal} employees assessed
                    </TextTiny>
                    <DistBar>
                        <DistSegment
                            $w={distSeg('Critical')}
                            $color={palette.vibrant.raspberry}
                        />
                        <DistSegment
                            $w={distSeg('High')}
                            $color={palette.vibrant.red}
                        />
                        <DistSegment
                            $w={distSeg('Medium')}
                            $color={palette.vibrant.orange}
                        />
                        <DistSegment
                            $w={distSeg('Low')}
                            $color={palette.evergreen.aspargus}
                        />
                    </DistBar>
                    <FlexDiv
                        gap={16}
                        style={{
                            fontSize: 11,
                            color: palette.graphite.charcoal,
                        }}
                    >
                        <span>
                            Critical · {distribution.Critical || 0}
                        </span>
                        <span>High · {distribution.High || 0}</span>
                        <span>Medium · {distribution.Medium || 0}</span>
                        <span>Low · {distribution.Low || 0}</span>
                    </FlexDiv>
                </Block>

                <Block padding="20" column>
                    <SpaceBetweenDiv>
                        <Heading3>Highest exposure employees</Heading3>
                    </SpaceBetweenDiv>
                    {topHigh.length === 0 ? (
                        <TextDefault lighter>
                            {loadingDashboard
                                ? 'Loading…'
                                : 'No completed runs yet.'}
                        </TextDefault>
                    ) : (
                        <Table>
                            <thead>
                                <tr>
                                    <th>Employee</th>
                                    <th>Score</th>
                                    <th>Level</th>
                                </tr>
                            </thead>
                            <tbody>
                                {topHigh.slice(0, 5).map((row, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <div
                                                style={{
                                                    fontWeight: 500,
                                                    color:
                                                        palette.graphite
                                                            .onyx,
                                                }}
                                            >
                                                {row.name || row.email}
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: 11,
                                                    color:
                                                        palette.graphite
                                                            .ash,
                                                }}
                                            >
                                                {row.email}
                                            </div>
                                        </td>
                                        <td>
                                            <span
                                                style={{
                                                    fontWeight: 600,
                                                    color: scoreColor(
                                                        row.exposure_score
                                                    ),
                                                }}
                                            >
                                                {row.exposure_score}
                                            </span>
                                        </td>
                                        <td>
                                            {levelBadge(row.exposure_level)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Block>
            </SplitRow>

            <Block padding="20" column>
                <SpaceBetweenDiv>
                    <Heading3>Recent campaigns</Heading3>
                    <Link
                        to="/aware-expose/campaigns"
                        style={{ fontSize: 13 }}
                    >
                        View all →
                    </Link>
                </SpaceBetweenDiv>
                {recent.length === 0 ? (
                    <TextDefault lighter>
                        {loadingDashboard
                            ? 'Loading campaigns…'
                            : 'No campaigns yet — click "+ New campaign" to get started.'}
                    </TextDefault>
                ) : (
                    <Table>
                        <thead>
                            <tr>
                                <th>Campaign</th>
                                <th>Launched</th>
                                <th>Audience</th>
                                <th>Completion</th>
                                <th>Avg score</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {recent.map((c) => {
                                const completion =
                                    c.invites_count > 0
                                        ? Math.round(
                                              (c.completed_count /
                                                  c.invites_count) *
                                                  100
                                          )
                                        : 0;
                                return (
                                    <tr key={c.id}>
                                        <td
                                            style={{
                                                fontWeight: 500,
                                                color:
                                                    palette.graphite.onyx,
                                            }}
                                        >
                                            <Link
                                                to={`/aware-expose/campaigns/${c.id}`}
                                                style={{
                                                    color:
                                                        palette.graphite
                                                            .onyx,
                                                }}
                                            >
                                                {c.name}
                                            </Link>
                                        </td>
                                        <td>
                                            {c.launched_at
                                                ? new Date(
                                                      c.launched_at
                                                  ).toLocaleDateString()
                                                : '—'}
                                        </td>
                                        <td>{c.invites_count || 0}</td>
                                        <td>{completion}%</td>
                                        <td>
                                            <span
                                                style={{
                                                    color: scoreColor(
                                                        c.avg_score
                                                    ),
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {c.avg_score == null
                                                    ? '—'
                                                    : c.avg_score}
                                            </span>
                                        </td>
                                        <td>{statusBadge(c.status)}</td>
                                        <td>
                                            <Link
                                                to={`/aware-expose/campaigns/${c.id}`}
                                                style={{ fontSize: 12 }}
                                            >
                                                View
                                            </Link>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                )}
            </Block>
        </Container>
    );
}

export default observer(AwareExposeDashboard);
